<template>
  <div class="page-tag-tab" :class="{ 'page-tag-tab--filled': selection.length }">
    <el-empty
      v-if="!selection.length"
      description="请返回上一步选择要打标的资源"
      :image-size="88"
    >
      <div class="page-tag-tab__emptyHint">
        范围已在第 1 步确认；大批量可切换到「Excel 打标」
      </div>
    </el-empty>

    <template v-else>
      <!-- 更新模式置顶：与 Excel 打标一致，配置前先看模式 -->
      <section class="page-tag-tab__mode">
        <span class="page-tag-tab__modeLabel">更新模式</span>
        <el-radio-group :value="mode" size="small" @input="handleModeChange">
          <el-radio
            v-for="item in modeOptions"
            :key="item.value"
            :label="item.value"
          >
            {{ item.label }}
            <el-tooltip :content="item.tip" placement="top" popper-class="genericResourceTooltip">
              <i class="el-icon-question" />
            </el-tooltip>
          </el-radio>
        </el-radio-group>
      </section>

      <!-- Tag 选择：占满剩余高度；已选 Tag 在上方 dock 查看 -->
      <section class="page-tag-tab__picker">
        <div class="page-tag-tab__panelHeader">
          <span class="page-tag-tab__panelTitle">选择 Tag</span>
          <span class="page-tag-tab__panelMeta">共 {{ selection.length }} 项资源</span>
        </div>
        <div class="page-tag-tab__pickerBody">
          <tag-picker v-model="selectedTags" />
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import TagPicker from './tagPicker.vue'
import { TAG_BIND_MODE, TAG_BIND_MODE_OPTIONS } from './constants'

export default {
  name: 'PageTagTab',
  components: { TagPicker },
  props: {
    selection: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Object,
      default: () => ({
        selectedTags: [],
        mode: TAG_BIND_MODE.APPEND,
      }),
    },
  },
  data() {
    return {
      modeOptions: TAG_BIND_MODE_OPTIONS,
      selectedTags: this.value.selectedTags || [],
      mode: this.value.mode || TAG_BIND_MODE.APPEND,
    }
  },
  watch: {
    selectedTags() {
      this.emitChange()
    },
    value(next) {
      this.selectedTags = next.selectedTags || []
      this.mode = next.mode || TAG_BIND_MODE.APPEND
    },
  },
  methods: {
    /** 更新模式文案，用于切换确认提示 */
    modeLabel(modeValue) {
      const option = this.modeOptions.find((item) => item.value === modeValue)
      return option ? option.label : modeValue
    },
    /**
     * 切换更新模式：已选 Tag 时二次确认，避免误切导致当前配置丢失。
     * 未选 Tag 时直接切换，不打断操作。
     */
    handleModeChange(nextMode) {
      if (nextMode === this.mode) {
        return
      }
      if (!this.selectedTags.length) {
        this.mode = nextMode
        this.emitChange()
        return
      }
      const fromLabel = this.modeLabel(this.mode)
      const toLabel = this.modeLabel(nextMode)
      this.$confirm(
        `当前为「${fromLabel}」模式且已选 ${this.selectedTags.length} 个 Tag。切换为「${toLabel}」将清空已选 Tag，当前操作不会保留。是否继续？`,
        '切换更新模式',
        {
          confirmButtonText: '继续切换',
          cancelButtonText: '取消',
          type: 'warning',
        }
      )
        .then(() => {
          this.selectedTags = []
          this.mode = nextMode
          this.emitChange()
        })
        .catch(() => {})
    },
    emitChange() {
      this.$emit('input', {
        selectedTags: this.selectedTags,
        mode: this.mode,
      })
      this.$emit('change', {
        selectedTags: this.selectedTags,
        mode: this.mode,
      })
    },
    validate() {
      if (!this.selection.length) {
        return '请返回上一步选择要打标的资源'
      }
      if (
        (this.mode === TAG_BIND_MODE.APPEND || this.mode === TAG_BIND_MODE.REMOVE) &&
        !this.selectedTags.length
      ) {
        return this.mode === TAG_BIND_MODE.APPEND
          ? '追加模式至少选择一个 Tag'
          : '移除模式至少选择一个 Tag'
      }
      return ''
    },
    buildItems() {
      const tagNames = this.selectedTags.map((tag) => tag.name)
      return this.selection.map((row) => ({
        resourceKey: row.key,
        title: row.resourceName || row.wallpaperName,
        resourceType: row.assetType || row.wallpaperType,
        tagNames,
      }))
    },
  },
}
</script>

<style scoped>
.page-tag-tab {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.page-tag-tab--filled {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  height: 100%;
  box-sizing: border-box;
}

.page-tag-tab__emptyHint {
  margin-top: 4px;
  font-size: var(--link-text-sm);
  color: var(--link-ink-muted);
}

.page-tag-tab__mode {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--link-surface-muted);
  border: 1px solid var(--link-line-strong);
}

.page-tag-tab__modeLabel {
  flex-shrink: 0;
  font-size: var(--link-text-md);
  font-weight: var(--link-font-semibold);
  color: var(--link-ink);
}

.page-tag-tab__panelHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
  flex-shrink: 0;
}

.page-tag-tab__panelTitle {
  font-size: var(--link-text-md);
  font-weight: var(--link-font-semibold);
  color: var(--link-ink);
}

.page-tag-tab__panelMeta {
  font-size: var(--link-text-xs);
  color: var(--link-ink-muted);
  white-space: nowrap;
}

.page-tag-tab__picker {
  flex: 1 1 auto;
  height: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.page-tag-tab__pickerBody {
  flex: 1 1 auto;
  height: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.page-tag-tab__pickerBody >>> .tag-picker {
  flex: 1 1 auto;
  height: 0;
  min-height: 0;
}
</style>
